import React, { useEffect, useState } from "react";
import { Mail, Phone, MapPin, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { publicApi, formatApiErrorDetail } from "@/lib/api";
import { SectionHeading } from "@/components/public/ui";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const INQUIRY_TYPES = ["General Inquiry", "Private Events", "Media / Press", "Careers / Employment", "Corporate Inquiry", "Property Question"];
const empty = { name: "", email: "", phone: "", inquiry_type: "General Inquiry", property: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [properties, setProperties] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { publicApi.get("/properties").then((r) => setProperties(r.data)).catch(() => {}); }, []);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await publicApi.post("/contact", { ...form, property: form.property === "none" ? "" : form.property });
      setDone(true);
      toast.success("Thank you — your message has been received.");
      setForm(empty);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send message");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pt-20">
      <section className="bg-[#18344A] py-20 text-white">
        <div className="mx-auto max-w-[1280px] px-5 lg:px-8">
          <SectionHeading eyebrow="Get in Touch" title="Let's Connect" light
            intro="Whether you're planning a celebration, a story, or simply have a question — we'd love to hear from you." />
        </div>
      </section>

      <section className="mx-auto max-w-[1100px] px-5 py-16 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-[1.4fr_1fr]">
          <div>
            {done ? (
              <div data-testid="contact-success" className="flex flex-col items-center rounded-2xl border border-[var(--pb-border)] bg-[#F5F3EF] p-12 text-center">
                <CheckCircle2 className="h-12 w-12 text-[#526E7E]" />
                <h3 className="mt-4 font-serif text-2xl text-[#18344A]">Message received</h3>
                <p className="mt-2 text-sm text-[rgba(48,56,60,0.7)]">Thank you for reaching out. A member of our team will be in touch soon.</p>
                <Button onClick={() => setDone(false)} className="mt-6 rounded-full bg-[#18344A] text-white hover:bg-[#18344A]/90">Send another message</Button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                <div className="grid gap-5 sm:grid-cols-2">
                  <div className="space-y-1.5"><Label>Name</Label><Input required data-testid="contact-name" value={form.name} onChange={(e) => set("name")(e.target.value)} className="h-11" /></div>
                  <div className="space-y-1.5"><Label>Email</Label><Input type="email" required data-testid="contact-email" value={form.email} onChange={(e) => set("email")(e.target.value)} className="h-11" /></div>
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <div className="space-y-1.5"><Label>Phone (optional)</Label><Input data-testid="contact-phone" value={form.phone} onChange={(e) => set("phone")(e.target.value)} className="h-11" /></div>
                  <div className="space-y-1.5">
                    <Label>Inquiry Type</Label>
                    <Select value={form.inquiry_type} onValueChange={set("inquiry_type")}>
                      <SelectTrigger data-testid="contact-inquiry-type" className="h-11"><SelectValue /></SelectTrigger>
                      <SelectContent>{INQUIRY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Property (optional)</Label>
                  <Select value={form.property || "none"} onValueChange={set("property")}>
                    <SelectTrigger data-testid="contact-property" className="h-11"><SelectValue placeholder="Select a property" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No specific property</SelectItem>
                      {properties.map((p) => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Message</Label><Textarea required rows={5} data-testid="contact-message" value={form.message} onChange={(e) => set("message")(e.target.value)} /></div>
                <Button type="submit" disabled={busy} data-testid="contact-submit" className="h-11 rounded-full bg-[#18344A] px-8 text-white hover:bg-[#18344A]/90">
                  {busy ? "Sending…" : "Send Message"}
                </Button>
              </form>
            )}
          </div>

          <aside className="h-fit rounded-2xl border border-[var(--pb-border)] bg-[#F5F3EF] p-7">
            <h3 className="font-serif text-2xl text-[#18344A]">Pack Brothers Hospitality</h3>
            <div className="mt-5 space-y-4 text-sm text-[rgba(48,56,60,0.8)]">
              <p className="flex items-start gap-3"><MapPin className="mt-0.5 h-4 w-4 text-[#526E7E]" /> Smithfield, Virginia</p>
              <p className="flex items-center gap-3"><Phone className="h-4 w-4 text-[#526E7E]" /> (757) 357-7700</p>
              <p className="flex items-center gap-3"><Mail className="h-4 w-4 text-[#526E7E]" /> hello@packbrothershospitality.com</p>
            </div>
            <div className="mt-6 border-t border-[var(--pb-border)] pt-5 text-sm text-[rgba(48,56,60,0.7)]">
              For reservations at a specific restaurant, please visit that property's page and booking link.
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
